import { Copy, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { UserWithRole } from '~entities/users/model/types';
import { useResetUserPassword, type ResetPasswordResponse } from '~entities/users/api/reset-password';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { copyToClipboard } from '~shared/utils/clipboard';

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithRole | null;
  onClose: () => void;
}

export const ResetPasswordDialog = ({
  open,
  onOpenChange,
  user,
  onClose,
}: ResetPasswordDialogProps) => {
  const resetPasswordMutation = useResetUserPassword();
  const [resetData, setResetData] = useState<ResetPasswordResponse['data'] | null>(null);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  const obfuscatePassword = (password: string): string => {
    if (password.length <= 4) {
      return '*'.repeat(password.length);
    }
    const lastFour = password.slice(-4);
    const obfuscated = '*'.repeat(password.length - 4);
    return obfuscated + lastFour;
  };

  const containerElementId = 'reset-password-dialog';
  const handleCopyToClipboard = async (text: string, field: 'email' | 'password') => {
    const success = await copyToClipboard(text, containerElementId);
    if (success) {
      setCopiedField(field);
      toast.success(`${field === 'email' ? 'Email' : 'Password'} copied to clipboard`);
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    } else {
      toast.error(`Failed to copy ${field}`);
    }
  };

  const handleReset = () => {
    if (!user?.id) {
      return;
    }

    resetPasswordMutation.mutate(user.id, {
      onSuccess: (data) => {
        setResetData(data.data);
      },
    });
  };

  const handleClose = () => {
    setResetData(null);
    setCopiedField(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id={containerElementId}>
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {resetData ? (
              <>
                Password has been reset successfully. The user will receive an email
                containing the new password. You can copy the details below:
              </>
            ) : (
              <>
                Are you sure you want to reset the password for {user?.firstName}{' '}
                {user?.lastName}? The user will receive an email containing the new
                password.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {resetData && (
          <div className='flex flex-col gap-4 py-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>Email</label>
              <div className='flex gap-2'>
                <Input
                  readOnly
                  value={resetData.email}
                  className='font-mono text-sm'
                />
                <Button
                  variant='secondary'
                  size='icon'
                  onClick={() => handleCopyToClipboard(resetData.email, 'email')}
                  aria-label='Copy email'
                  disabled={copiedField === 'email'}
                >
                  <Copy className='size-4' />
                </Button>
              </div>
            </div>

            <div className='space-y-2'>
              <label className='text-sm font-medium'>New Password</label>
              <div className='flex gap-2'>
                <Input
                  readOnly
                  value={obfuscatePassword(resetData.newPassword)}
                  className='font-mono text-sm'
                />
                <Button
                  variant='secondary'
                  size='icon'
                  onClick={() => handleCopyToClipboard(resetData.newPassword, 'password')}
                  aria-label='Copy password'
                  disabled={copiedField === 'password'}
                  title='Copy actual password (not obfuscated)'
                >
                  <Copy className='size-4' />
                </Button>
              </div>
              <p className='text-xs text-muted-foreground'>
                Last 4 characters are visible. Click copy to get the full password.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={resetPasswordMutation.isPending}
          >
            {resetData ? 'Close' : 'Cancel'}
          </Button>
          {!resetData && (
            <Button
              onClick={handleReset}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

